import axios from 'axios';
import main from './main';
import { canFallbackToLegacyLanding, normalizeLandingConfig } from './utils/landingPage';
import { showToast } from './utils/utils';
import { encrypt, generateRandomString, getCurrentTimestamp, insertAtPosition } from './utils/rsaUtils';

const api = axios.create({
  baseURL: '/api',
  timeout: 120000,
});

// 响应拦截器
api.interceptors.response.use(
  (res) => {
    let data = res.data;
    if (data.code === 500 && data?.msg && data?.msg.includes('登录状态已失效')) {
      window.location.href = '/order';
    }
    return Promise.resolve(res);
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// 获取请求省份
export async function apiGetProvinceByIp() {
  return await api.get('/card/client/getProvinceByIp', { params: { shopId: main.shopId } });
}
// 获取分享链接
export async function apiGoodsLandingPageLink(params: any) {
  return await api.get('/card/client/goodsLandingPageLink', { params });
}

// 获取商品列表
export async function apiGetGoodsList(params: any) {
  await ensureLandingApiMode();
  const path = main.landingApiMode === 'cardOrderLp' ? '/card/client/goodsListNew' : '/card/client/goodsList';
  return await api.get(path, {
    params: {
      ...params,
      shopId: main.shopId,
    },
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((item) => searchParams.append(key, String(item)));
          return;
        }
        searchParams.append(key, String(value));
      });
      return searchParams.toString();
    },
  });
}
// 获取人气推荐列表
export async function apiGetRecommendedGoods(params: any) {
  await ensureLandingApiMode();
  const path =
    main.landingApiMode === 'cardOrderLp' ? '/card/client/recommendedGoodsNew' : '/card/client/recommendedGoods';
  return await api.get(path, { params: { ...params, shopId: main.shopId } });
}
// 搜索-猜你想要
export async function apiGetSelectLikeGoods() {
  await ensureLandingApiMode();
  if (main.landingApiMode === 'cardOrderLp') {
    const res = await api.get('/card/client/goodsListNew', {
      params: { shopId: main.shopId, pageNum: 1, pageSize: 5 },
    });
    res.data = { ...res.data, data: res.data?.rows ?? [] };
    return res;
  }
  return await api.get('/card/client/selectLikeGoods', { params: { shopId: main.shopId } });
}
// 获取商品详情
export async function apiGetGoodsDetail(code: string, ci?: string, shopNum?: string) {
  return await api.get('/card/client/goodsDetail', { params: { code, ci, shopNum } });
}

// 查询订单列表
export async function apiGetOrderList(params: any) {
  return await api.get('/card/client/queryOrder', { params });
}

const landingConfigRequests = new Map<string, Promise<any>>();

async function loadH5Config(shopId: string) {
  try {
    const res = await api.get('/card/client/indexNew', { params: { shopId } });
    const isConfigured =
      res.data?.code === 200 && !!res.data?.data && typeof res.data.data === 'object';
    if (isConfigured) {
      if (main.shopId === shopId) main.setLandingApiMode('cardOrderLp');
      res.data = { ...res.data, data: normalizeLandingConfig(res.data.data) };
      return res;
    }

    if (!canFallbackToLegacyLanding(res.data)) return res;
  } catch (error: any) {
    // Spring 的业务校验在部分环境会返回 HTTP 400；仅明确“落地页不存在”时兼容旧链接。
    // 其他 400、网络超时和 500 必须继续抛出，不能用旧配置掩盖新链路故障。
    if (!canFallbackToLegacyLanding(error?.response?.data, error?.response?.status)) throw error;
  }

  const legacyRes = await api.get('/card/client/index', { params: { shopId } });
  if (legacyRes.data?.code === 200) {
    if (main.shopId === shopId) main.setLandingApiMode('legacy');
    legacyRes.data = {
      ...legacyRes.data,
      data: normalizeLandingConfig(legacyRes.data.data, { legacyEmptyTypesUseDefaults: true }),
    };
  }
  return legacyRes;
}

// 首页配置。同一落地页的并发调用共用一次探测，避免慢网下首页与搜索页各自判断出不同接口模式。
export function apiGetH5Config() {
  const shopId = main.shopId || sessionStorage.getItem('landingCode') || '';
  const pending = landingConfigRequests.get(shopId);
  if (pending) return pending;

  const request = loadH5Config(shopId).finally(() => {
    if (landingConfigRequests.get(shopId) === request) landingConfigRequests.delete(shopId);
  });
  landingConfigRequests.set(shopId, request);
  return request;
}

async function ensureLandingApiMode() {
  const shopId = main.shopId;
  const resolvedForCurrentCode =
    main.landingApiModeResolved && sessionStorage.getItem('landingApiModeCode') === shopId;
  if (resolvedForCurrentCode) return;

  await apiGetH5Config();
  if (!main.landingApiModeResolved || main.shopId !== shopId) {
    throw new Error('落地页配置尚未加载完成');
  }
}
// 提交订单
export async function apiSubmitOrder(body: any) {
  return await api.post('/card/client/submitOrder', body);
}
// 提交订单 支付
export async function apiPayAndSubmitOrder(body: any) {
  return await api.post('/card/client/payGoodsOrder', body);
}
// 获取地址库
export async function apiGetMatchById(supId: string) {
  return await api.get('/card/client/matchById', { params: { supId } });
}

// 获取号码池
export async function apiGetCardPool(goodsCode: string, province?: string, city?: string, county?: string) {
  return await api.get('/card/client/queryPhonePool', { params: { goodsCode, province, city, county } });
}

// 12306页面监控
export async function apiMonitorPage(id: string) {
  return await api.get(`/card/client/monitorPage/${id}`);
}
// 获取OPENID
export async function apiGetOpenId(code: string, appId: string, id: string) {
  return await api.get(`/card/common/getUserOpenId?code=${code}&appId=${appId}&id=${id}`);
}
// 获取支付配置列表
export async function apiGetPayConfigList(goodsCode: string) {
  return await api.get(`/card/client/getPayList`, { params: { goodsCode } });
}

// 通过编码获取订单编号
export async function apiClientGetOrderSn(code: string) {
  return await api.get(`/card/client/getOrderSn/${code}`);
}

/// 图片上传
export async function apiUploadPic(file: File, randomCode: string) {
  let res = await api.post(
    '/resource/oss/randomCode',
    { file, randomCode },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  let url: string | null = null;
  if (res.data.code === 200) {
    url = res.data.data.url;
  } else {
    showToast(res.data.msg ?? '上传失败');
  }

  return url;
}
// 提交订单-图片上传
export async function apiOssUpload(file: File) {
  let res = await api.post(
    '/card/client/oss/upload',
    { file },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  let url: string | null = null;
  if (res.data.code === 200) {
    url = res.data.data.url;
  } else {
    showToast(res.data.msg ?? '上传失败');
  }

  return url;
}

// 提交三证
export async function apiSavePhoto(body: any) {
  return await api.post(`/card/client/savePhoto`, body);
}

// 获取短信验证码
export async function apiMarketVerifyCode(phoneNum: string, goodsCode: string) {
  const timestamp = getCurrentTimestamp();
  const str = encrypt(phoneNum + timestamp) || '';
  const randomStr = generateRandomString(4);
  const NewStr = insertAtPosition(str, randomStr, 11);
  return await api.get('/card/client/market/verifyCode', { params: { phoneNum: NewStr, goodsCode } });
}

// 提交存量订单
export async function apiMarketOfferOrder(data: any) {
  return await api.get('/card/client/market/offerOrder', { params: data });
}

// 加密手机号
export async function apiEncryption(data: any) {
  let res = await api.get('/card/client/encryption', { params: data });
  return res.data;
}

// 申请退款
export async function apiApplyRefund(params: any) {
  let res = await api.get('/card/client/applyRefund', { params });
  return res.data;
}

// 取消申请退款
export async function apiCancelRefund(params: any) {
  let res = await api.get('/card/client/cancelRefund', { params });
  return res.data;
}

// 获取协议
export async function apiChinaMobilProtocol(params: any) {
  return await api.get('/card/client/chinaMobilProtocol', { params });
}

/// 获取token
export async function marketSelectToken(data: any) {
  var res = await api.post('/card/notify/dataNotifyHandler', data);
  return res.data;
}
/// 获取商品运费
export async function queryCarryPrice(data: any) {
  var res = await api.get('/card/client/queryCarryPrice', { params: data });
  return res.data;
}

/// 登录
export async function apiClienth5Login(params: any) {
  var res = await api.get('/card/client/h5Login', { params });
  return res.data;
}

// 查询订单列表-新
export async function apiGetH5QueryOrder() {
  return await api.get('/card/client/h5QueryOrder', {
    headers: {
      token: main?.token,
    },
  });
}
// 查询token是否有效
export async function apiGetH5LoginUser() {
  return await api.get('/card/client/getH5LoginUser', {
    headers: {
      token: main?.token,
    },
  });
}

// 查询订单物流轨迹
export async function apiGetH5QueryOrderTrack(params: any) {
  return await api.get('/card/client/h5QueryOrderTrack', {
    params,
    headers: {
      token: main?.token,
    },
  });
}

// 关闭订单
export async function apiGetH5CloseOrder(params: any) {
  return await api.get('/card/client/h5CloseOrder', {
    params,
    headers: {
      token: main?.token,
    },
  });
}

// 支付列表
export async function apiGetH5GetPayList() {
  return await api.get('/card/client/h5GetPayList', {
    headers: {
      token: main?.token,
    },
  });
}

// 查询订单-支付
export async function apiGetH5PayGoodsOrder(data: any) {
  return await api.post('/card/client/h5PayGoodsOrder', data, {
    headers: {
      token: main?.token,
    },
  });
}

// 申请售后详情
export async function apiGetH5RefundDetail(params: any) {
  return await api.get('/card/client/h5RefundDetail', {
    params,
    headers: {
      token: main?.token,
    },
  });
}

// 申请退款
export async function apiGetH5Refund(data: any) {
  return await api.post('/card/client/h5Refund', data, {
    headers: {
      token: main?.token,
    },
  });
}

// 取消申请退款
export async function apiGetH5RefundCancel(params: any) {
  return await api.get('/card/client/h5RefundCancel', {
    params,
    headers: {
      token: main?.token,
    },
  });
}

// 补传三证
export async function apiClientSavePhoto(data: any) {
  return await api.post('/card/client/savePhoto', data, {
    headers: {
      token: main?.token,
    },
  });
}

// 补传三证前获取randomNum短码
export async function apigetOrderPhotoH5(id: string) {
  return await api.get(`/card/client/getOrderPhotoH5/${id}`, {
    headers: {
      token: main?.token,
    },
  });
}

// 根据订单，查询地址信息
export async function apiGetH5GetOrderAddress(params: any) {
  return await api.get(`/card/client/h5GetOrderAddress`, {
    params,
    headers: {
      token: main?.token,
    },
  });
}

// 修改地址
export async function apiH5EditOrderAddress(data: any) {
  return await api.post('/card/client/h5EditOrderAddress', data, {
    headers: {
      token: main?.token,
    },
  });
}

// jsapi配置
export async function apiGetWeChatSignature(url: string, appId: string) {
  // return await api.get(`/client/common/getWeChatSignature`, { params: { url: encodeURIComponent(url), appId } });
  return await api.get(`/card/client/getWeChatSignature`, { params: { url: encodeURIComponent(url), appId } });
}
// 验证码
export async function apiGetCaptchaeImg() {
  return await api.get('/auth/captcha/image', {});
}
// h5RebateApply
export async function apiH5RebateApply(params: any) {
  return await api.get('/card/client/cardOrderRebateApplication/h5RebateApply', { params });
}

// 返费申请-发送短信验证码（激活号码、身份证加密传输）
export async function apiSendRefundFeeSmsCode(
  phonenumber: string,
  cardNum: string,
  captchaId: string,
  captchaCode: string
) {
  const timestamp = getCurrentTimestamp();
  const phoneStr = encrypt(phonenumber + timestamp) || '';
  const randomStr = generateRandomString(4);
  const encryptedPhone = insertAtPosition(phoneStr, randomStr, 11);
  const encryptedCardNum = encrypt(cardNum) || '';
  return await api.get('/card/client/cardOrderRebateApplication/sendSmsCode', {
    params: {
      phonenumber: encryptedPhone,
      cardNum: encryptedCardNum,
      captchaId,
      captchaCode,
    },
  });
}

// 模糊查询商品列表
export async function apiGetPreSearchGoods(params: any) {
  await ensureLandingApiMode();
  if (main.landingApiMode === 'cardOrderLp') {
    const res = await api.get('/card/client/goodsListNew', {
      params: { ...params, shopId: main.shopId, pageNum: 1, pageSize: 20 },
    });
    res.data = { ...res.data, data: res.data?.rows ?? [] };
    return res;
  }
  return await api.get('/card/client/preSearchGoods', { params: { ...params, shopId: main.shopId } });
}
